import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Tooltip } from 'antd';
import { Button, Card, TextField } from '../../design-system';
import { AutoCorrelationScopeMode } from '../../@types/projects/types';
import { ProjectSample } from '../../@types/samples/types';
import {
  AutoCorrelationFamily,
  ScopeSampleUids,
  useAutoCorrelation
} from '../../providers/AutoCorrelation';
import { useCorrelation } from '../../providers/Correlation';
import { useMultiAnalysis } from '../../providers/MultiAnalysis';
// Reuse Multi Analysis's real-data logic (not `performed_analyses`, which the
// backend flags even for skipped calculations) so only samples that can
// actually contribute a fit are offered per family.
import { hasResultForStrategy } from '../../pages/MultiAnalysisPage/analysisCategory';
import {
  FAMILY_KEYS,
  FAMILY_LABELS,
  FAMILY_STRATEGY
} from './autoCorrelationUtils';
import styles from './AutoCorrelation.module.css';

const MIN_SAMPLES_PER_FAMILY = 3;

const SCOPE_MODE_OPTIONS: {
  value: AutoCorrelationScopeMode;
  label: string;
}[] = [
  { value: 'all', label: 'All project samples' },
  { value: 'multi_analysis', label: 'Multi Analysis samples' },
  { value: 'current_correlation', label: 'Current correlation samples' },
  { value: 'manual', label: 'Manual pick' }
];

const FewSamplesWarning = () => (
  <Tooltip title="Fewer than 3 samples: the fitted trends will be statistically fragile.">
    <span className={styles.familyWarning}>
      ⚠ fewer than {MIN_SAMPLES_PER_FAMILY} samples
    </span>
  </Tooltip>
);

/**
 * Step 1 — choose which samples feed the automatic correlation, per family.
 * Non-manual modes resolve the uid lists from the correlation working copy;
 * manual mode exposes per-family sample checkbox grids (scrollable) with a
 * sample-code filter.
 */
const ScopeStep = () => {
  const { scopeMode, scopeSampleUids, setScope } = useAutoCorrelation();
  const { projectSamples } = useCorrelation();
  const multiAnalysis = useMultiAnalysis();

  const [sampleFilter, setSampleFilter] = useState('');

  const multiAnalysisUids = useMemo(
    () => new Set(multiAnalysis.samples.map(sample => sample.uid)),
    [multiAnalysis.samples]
  );

  /** Samples eligible for each family — those with a real computed result
   *  the correlation can consume (not merely flagged as "performed"). */
  const familySamples = useMemo(() => {
    const result = {} as Record<AutoCorrelationFamily, ProjectSample[]>;
    FAMILY_KEYS.forEach(family => {
      result[family] = projectSamples.filter(sample =>
        hasResultForStrategy(sample, FAMILY_STRATEGY[family])
      );
    });
    return result;
  }, [projectSamples]);

  const resolveUids = useCallback(
    (mode: AutoCorrelationScopeMode): ScopeSampleUids => {
      const uids = {} as ScopeSampleUids;
      FAMILY_KEYS.forEach(family => {
        let samples = familySamples[family];
        if (mode === 'current_correlation') {
          samples = samples.filter(
            sample => sample.selectedAnalyses?.[FAMILY_STRATEGY[family]]
          );
        }
        if (mode === 'multi_analysis' && multiAnalysisUids.size > 0) {
          samples = samples.filter(sample => multiAnalysisUids.has(sample.uid));
        }
        uids[family] = samples.map(sample => sample.uid);
      });
      return uids;
    },
    [familySamples, multiAnalysisUids]
  );

  // Keep the resolved uid lists in sync while a non-manual mode is active —
  // project samples load asynchronously after the wizard may already be open.
  // Only push when the resolution actually differs, otherwise fresh array
  // identities from the source hooks would loop this effect forever.
  useEffect(() => {
    if (scopeMode === 'manual' || scopeMode === 'filtered') return;
    const resolved = resolveUids(scopeMode);
    const changed = FAMILY_KEYS.some(
      family => resolved[family].join('|') !== scopeSampleUids[family].join('|')
    );
    if (changed) setScope(scopeMode, resolved);
  }, [scopeMode, resolveUids, setScope, scopeSampleUids]);

  const handleModeChange = (mode: AutoCorrelationScopeMode) => {
    if (mode === 'manual') {
      // Seed the manual pick from the current resolution (or everything).
      const hasAny = FAMILY_KEYS.some(
        family => scopeSampleUids[family].length > 0
      );
      setScope('manual', hasAny ? scopeSampleUids : resolveUids('all'));
      return;
    }
    setScope(mode, resolveUids(mode));
  };

  const toggleManualSample = (family: AutoCorrelationFamily, uid: string) => {
    const current = scopeSampleUids[family];
    const next = current.includes(uid)
      ? current.filter(entry => entry !== uid)
      : [...current, uid];
    setScope('manual', { ...scopeSampleUids, [family]: next });
  };

  const setFamilySelection = (
    family: AutoCorrelationFamily,
    uids: string[]
  ) => {
    setScope('manual', { ...scopeSampleUids, [family]: uids });
  };

  const filterText = sampleFilter.trim().toLowerCase();
  const matchesFilter = (sample: ProjectSample) =>
    filterText === '' ||
    (sample.sample_code ?? '').toLowerCase().includes(filterText);

  const multiAnalysisFallback =
    scopeMode === 'multi_analysis' && multiAnalysisUids.size === 0;

  const isManual = scopeMode === 'manual';

  return (
    <div>
      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>Sample scope</h3>
        <p className={styles.sectionSubtitle}>
          Choose which samples feed the automatic correlation. Families
          without samples are skipped.
        </p>

        <div className={styles.scopeToolbar}>
          <div
            className={styles.modeGroup}
            role="group"
            aria-label="Sample scope mode"
            data-testid="auto-scope-mode"
          >
            {SCOPE_MODE_OPTIONS.map(option => (
              <Button
                key={option.value}
                variant={scopeMode === option.value ? 'primary' : 'secondary'}
                size="sm"
                aria-pressed={scopeMode === option.value}
                onClick={() => handleModeChange(option.value)}
              >
                {option.label}
              </Button>
            ))}
          </div>

          {isManual ? (
            <TextField
              className={styles.searchField}
              size="sm"
              placeholder="Filter by sample code…"
              value={sampleFilter}
              onChange={event => setSampleFilter(event.target.value)}
              data-testid="auto-sample-filter"
            />
          ) : null}
        </div>

        {multiAnalysisFallback ? (
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: '12px' }}
            message="No Multi Analysis samples in memory — using all project samples with the analysis performed."
          />
        ) : null}
      </div>

      <div className={styles.familyGrid}>
        {FAMILY_KEYS.map(family => {
          const available = familySamples[family];
          const selectedCount = scopeSampleUids[family].length;
          const countSummary = (
            <span data-testid={`auto-scope-count-${family}`}>
              {selectedCount === 0 ? (
                <span className={styles.familySkipped}>
                  0 samples — family skipped
                </span>
              ) : (
                <span className={styles.familyCount}>
                  {selectedCount}/{available.length} sample
                  {available.length === 1 ? '' : 's'}{' '}
                  {selectedCount < MIN_SAMPLES_PER_FAMILY ? (
                    <FewSamplesWarning />
                  ) : null}
                </span>
              )}
            </span>
          );

          if (!isManual) {
            // Compact summary card — the uid lists are resolved automatically.
            return (
              <Card key={family} variant="outlined" padding="sm" fullWidth>
                <Card.Header
                  title={FAMILY_LABELS[family]}
                  action={countSummary}
                />
              </Card>
            );
          }

          const visibleSamples = available.filter(matchesFilter);

          return (
            <Card key={family} variant="outlined" padding="sm" fullWidth>
              <Card.Header
                title={FAMILY_LABELS[family]}
                subtitle={countSummary}
                action={
                  available.length > 0 ? (
                    <div className={styles.familyCardActions}>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() =>
                          setFamilySelection(
                            family,
                            available.map(sample => sample.uid)
                          )
                        }
                      >
                        Select all
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setFamilySelection(family, [])}
                      >
                        Clear
                      </Button>
                    </div>
                  ) : undefined
                }
              />
              <Card.Body>
                {available.length === 0 ? (
                  <p className={styles.emptyFilterNote}>
                    No samples with this analysis in the project.
                  </p>
                ) : visibleSamples.length === 0 ? (
                  <p className={styles.emptyFilterNote}>
                    No sample codes match “{sampleFilter.trim()}”.
                  </p>
                ) : (
                  <div
                    className={styles.sampleGrid}
                    data-testid={`auto-sample-grid-${family}`}
                  >
                    {visibleSamples.map(sample => (
                      <label key={sample.uid} className={styles.sampleItem}>
                        <input
                          type="checkbox"
                          checked={scopeSampleUids[family].includes(
                            sample.uid
                          )}
                          onChange={() =>
                            toggleManualSample(family, sample.uid)
                          }
                        />
                        <span className={styles.sampleItemLabel}>
                          {sample.sample_code}
                        </span>
                      </label>
                    ))}
                  </div>
                )}
              </Card.Body>
            </Card>
          );
        })}
      </div>
    </div>
  );
};

export default ScopeStep;
